import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getPayPalTransactions } from '../../endpoints/financePayments';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import type { PayPalTransactionRow } from '../../types/financePayments';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const PaymentsPayPalPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, isLoading } = useApiGet(
    ['finance-payments-paypal', page, rowsPerPage],
    () =>
      getPayPalTransactions({
        page: page + 1,
        page_size: rowsPerPage,
      }),
    { enabled: true, retry: 1 }
  );

  const tableRows = data?.data ?? [];
  const pagination = data?.pagination;

  const columns: ServerSideTableColumn<PayPalTransactionRow>[] = [
    {
      id: 'paypal_transaction_id',
      label: 'Transaction',
      width: '18%',
      firstCell: true,
      sortable: false,
      render: (_v, row) => (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {row.paypal_transaction_id}
        </Typography>
      ),
    },
    {
      id: 'source_kind',
      label: 'Source',
      width: '10%',
      sortable: false,
      render: (_v, row) => row.source_kind,
    },
    {
      id: 'is_recurring',
      label: 'Schedule',
      width: '10%',
      sortable: false,
      render: (_v, row) => (row.is_recurring ? 'Recurring' : 'Ad-hoc'),
    },
    {
      id: 'amount',
      label: 'Amount',
      width: '10%',
      sortable: false,
      render: (_v, row) =>
        `${fmtMoney(row.amount)} ${row.currency_code || ''}`.trim(),
    },
    {
      id: 'elf_loan_id',
      label: 'ELF loan',
      width: '12%',
      sortable: false,
      render: (_v, row) => row.elf_loan_id || '—',
    },
    {
      id: 'transaction_time',
      label: 'Time',
      width: '14%',
      sortable: false,
      render: (_v, row) =>
        row.transaction_time
          ? new Date(row.transaction_time).toLocaleString()
          : '—',
    },
    {
      id: 'status',
      label: 'Status',
      width: '10%',
      sortable: false,
      render: (_v, row) => row.status || '—',
    },
    {
      id: 'description',
      label: 'Description',
      width: '20%',
      sortable: false,
      render: (_v, row) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
          {row.description || '—'}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        PayPal settlement transactions (read-only). Column set is a starting
        point—adjust based on finance feedback.
      </Typography>
      <ServerSideTable<PayPalTransactionRow>
        data={tableRows}
        columns={columns}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={pagination?.total_count ?? 0}
        totalPages={pagination?.total_pages ?? 0}
        hasNext={pagination?.has_next ?? false}
        hasPrev={pagination?.has_prev ?? false}
        order="desc"
        orderBy="transaction_time"
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        onSort={() => {}}
        emptyMessage="No PayPal transactions found."
      />
    </Box>
  );
};

export default PaymentsPayPalPage;
